import { useAtom, useAtomValue } from 'jotai'
import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { getInstalledChannel, isAlpha } from '@shared/utils/builds/channels.shared'
import {
  resolveSidepanelProfile,
  type ResolvedSidepanelProfile,
  type SidepanelProfileOverride,
  type SidepanelSurface,
  type SidepanelSurfaceVariant,
} from './sidepanel-profile'

/** Settings is a global store, so a pick in the Settings window reaches the overlay at once. */
const sidepanelProfileOverrideAtom = atomWithGlobalStore<SidepanelProfileOverride | null>(
  'settings',
  'sidepanel.profileOverride',
  null,
)

/** Development and alpha builds honour the override. Other builds ignore whatever is stored. */
export const canOverrideSidepanelProfile = (): boolean => isAlpha()

export const useSidepanelProfileOverride = () => useAtom(sidepanelProfileOverrideAtom)

/**
 * The sidepanel this build and person get. The flag is read live, so every surface agrees on the
 * profile even though they mount at different times.
 */
export function useSidepanelProfile(): ResolvedSidepanelProfile {
  const override = useAtomValue(sidepanelProfileOverrideAtom)
  const flags = useFlags({ live: true })
  return resolveSidepanelProfile({
    channel: getInstalledChannel(),
    allowsOverride: canOverrideSidepanelProfile(),
    override,
    // `false` reads as no variant, so an unready or missing flag reports the default, not the flag.
    readFlagVariant: () => flags.getValue(FEATURE_FLAGS.SIDEPANEL_PROFILE, false),
  })
}

/** The design one surface renders. Read it only in the component that switches that surface. */
export function useSidepanelSurface<S extends SidepanelSurface>(surface: S): SidepanelSurfaceVariant<S> {
  return useSidepanelProfile().surfaces[surface]
}
